import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { SessionService } from '../../core/session/session.service';
import { LoggingService } from '../../core/services/logging.service';
import { CcNumbersService } from '../cc-numbers/cc-numbers.service';
import { NumberTag } from '../cc-numbers/cc-numbers.model';
import { ReachabilitiesService } from '../reachabilities/reachabilities.service';
import { Reachability, ReachabilityType, REACHABILITY_NO_ID_ERROR } from '../reachabilities/reachabilities.model';
import { NotificationsForwardingsService } from './notifications-forwardings.service';
import { planOrderSwap } from './forwarding-order.util';
import {
  DirectForwardingGroup,
  DirectForwardingNumber,
  formatDayOfWeek,
  formatSchedule,
  Forwarding,
  Notification,
} from './notifications-forwardings.model';
import { ScheduleEditDialogComponent, ScheduleEditDialogResult } from './schedule-edit-dialog.component';
import { AddReachabilityDialogComponent, AddReachabilityDialogResult } from './add-reachability-dialog.component';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { TypeFilterComponent } from '../../shared/type-filter/type-filter.component';
import { CappedListComponent } from '../../shared/capped-list/capped-list.component';
import { RevealOnExpandDirective } from '../../shared/directives/reveal-on-expand.directive';
import { TruncateTokenPipe, isTruncated } from '../../shared/pipes/truncate-token.pipe';
import { typeBadgeVariant } from '../../shared/utils/reachability-type-badge';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import { SNACKBAR_ERROR_CONFIG } from '../../shared/utils/notification-defaults';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  HlmTooltipDirective,
  HLM_MENU,
} from '../../ui';

/**
 * Reachability types a user may CREATE per section, mirroring the old CMT
 * restrictions (verified against customer-management-tool):
 * call notifications → email/fax/sms, manual → phone/mobile, busy → BUSY,
 * direct → DIREKT, AM → email only.
 */
const SECTION_ALLOWED_TYPES: Record<string, string[]> = {
  callNotif: ['EMAIL', 'FAX', 'SMS'],
  manual: ['TELEFON', 'HANDY'],
  busy: ['BUSY'],
  direct: ['DIREKT'],
  am: ['EMAIL'],
};

@Component({
  selector: 'cmt-notifications-forwardings',
  imports: [
    ErrorStateComponent,
    HlmBadgeComponent, HlmBtnDirective, HlmIconComponent,
    HlmInputDirective, HlmSpinnerComponent, HlmTooltipDirective,
    TruncateTokenPipe, TypeFilterComponent, CappedListComponent,
    RevealOnExpandDirective,
    ...HLM_MENU,
  ],
  templateUrl: './notifications-forwardings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsForwardingsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(NotificationsForwardingsService);
  private readonly ccApi = inject(CcNumbersService);
  private readonly reachApi = inject(ReachabilitiesService);
  private readonly session = inject(SessionService);
  private readonly toast = inject(ToastService);
  private readonly loggingService = inject(LoggingService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** A reorder (writes + the reload that follows) is in flight — move buttons stay disabled until the reloaded list is in. */
  readonly reordering = signal(false);

  // Section data
  readonly callNotifications = signal<Notification[]>([]);
  readonly manualForwardings = signal<Forwarding[]>([]);
  readonly busyForwardings = signal<Forwarding[]>([]);
  readonly directForwardingGroups = signal<DirectForwardingGroup[]>([]);
  readonly amNotifications = signal<Notification[]>([]);

  // Phone numbers loaded from API
  readonly phoneNumbers = signal<{ number: string; type: string; tags?: NumberTag[]; userAccountId?: number }[]>([]);

  // AM active check
  readonly amActive = computed(() =>
    this.phoneNumbers().some(n => n.type === 'VOICEBOX'),
  );

  // ALL numbers for direct forwardings (not just CALLCENTER/HIDDEN)
  readonly directForwardingNumbers = computed<DirectForwardingNumber[]>(() =>
    this.phoneNumbers().map(n => ({ number: n.number, type: n.type, tags: n.tags ?? [] })),
  );

  // Filter
  readonly filterText = signal('');

  // Type quick-filter — null until the user touches it, then an explicit set.
  // Default (null) shows every type except PUSH, which is the noisy one.
  readonly selectedTypes = signal<string[] | null>(null);

  /** Distinct reachability types present across all sections, in first-seen order. */
  readonly availableTypes = computed<string[]>(() => {
    const seen = new Set<string>();
    const push = (t: string) => seen.add(t);
    this.callNotifications().forEach(n => push(n.reachability.type));
    this.manualForwardings().forEach(f => push(f.reachability.type));
    this.busyForwardings().forEach(f => push(f.reachability.type));
    this.directForwardingGroups().forEach(g => g.forwardings.forEach(f => push(f.reachability.type)));
    this.amNotifications().forEach(n => push(n.reachability.type));
    return [...seen];
  });

  /** Effective selected types — defaults to everything except PUSH. */
  readonly effectiveSelectedTypes = computed<string[]>(() =>
    this.selectedTypes() ?? this.availableTypes().filter(t => t !== 'PUSH'),
  );

  private typeVisible(type: string): boolean {
    return this.effectiveSelectedTypes().includes(type);
  }

  // Filtered views
  readonly filteredCallNotifications = computed(() => this.filterNotifications(this.callNotifications()));
  readonly filteredManualForwardings = computed(() => this.filterForwardings(this.manualForwardings()));
  readonly filteredBusyForwardings = computed(() => this.filterForwardings(this.busyForwardings()));

  /**
   * A filter hides some rows of a section that still shows at least two — a move then swaps with
   * the next VISIBLE row and jumps the hidden ones, so the operator is warned.
   * @ticket EB-4395
   */
  readonly manualReorderFiltered = computed(() =>
    this.filteredManualForwardings().length > 1
    && this.filteredManualForwardings().length < this.manualForwardings().length);
  readonly busyReorderFiltered = computed(() =>
    this.filteredBusyForwardings().length > 1
    && this.filteredBusyForwardings().length < this.busyForwardings().length);
  readonly filteredDirectGroups = computed(() => {
    const q = this.filterText().toLowerCase();
    return this.directForwardingGroups().map(g => ({
      ...g,
      forwardings: g.forwardings.filter(f =>
        this.typeVisible(f.reachability.type) && (!q || this.matchesFilter(f.reachability, q)),
      ),
      /** Unfiltered forwarding count, for the "N of M" hint. */
      totalCount: g.forwardings.length,
    }));
  });
  readonly filteredAmNotifications = computed(() => this.filterNotifications(this.amNotifications()));

  /**
   * "N of M" hint per section, shown next to the title only while filters hide rows.
   * Null when everything is visible — a plain total adds no information there.
   */
  readonly reducedCounts = computed<Record<string, string | null>>(() => {
    const sum = (groups: { forwardings: unknown[] }[]) => groups.reduce((acc, g) => acc + g.forwardings.length, 0);
    return {
      callNotif: this.shownOf(this.filteredCallNotifications().length, this.callNotifications().length),
      manual: this.shownOf(this.filteredManualForwardings().length, this.manualForwardings().length),
      busy: this.shownOf(this.filteredBusyForwardings().length, this.busyForwardings().length),
      direct: this.shownOf(sum(this.filteredDirectGroups()), sum(this.directForwardingGroups())),
      am: this.shownOf(this.filteredAmNotifications().length, this.amNotifications().length),
    };
  });

  /** "N of M" when filters hide rows, otherwise null. */
  shownOf(shown: number, total: number): string | null {
    return shown < total ? `${shown} of ${total}` : null;
  }

  // Sections always render so their header (and the Add action) stay reachable
  // even when active filters hide every row.
  readonly sectionHasResults = computed(() => ({
    callNotif: true, manual: true, busy: true, direct: true, am: true,
  }));

  private readonly filtersActive = computed(() =>
    !!this.filterText() || this.effectiveSelectedTypes().length < this.availableTypes().length,
  );

  /** Count of non-empty sections whose rows are all hidden by the active filters. */
  readonly hiddenSectionCount = computed(() => {
    if (!this.filtersActive()) return 0;
    let hidden = 0;
    if (this.callNotifications().length > 0 && this.filteredCallNotifications().length === 0) hidden++;
    if (this.manualForwardings().length > 0 && this.filteredManualForwardings().length === 0) hidden++;
    if (this.busyForwardings().length > 0 && this.filteredBusyForwardings().length === 0) hidden++;
    if (this.directForwardingGroups().some(g => g.forwardings.length > 0)
        && this.filteredDirectGroups().every(g => g.forwardings.length === 0)) hidden++;
    if (this.amNotifications().length > 0 && this.filteredAmNotifications().length === 0) hidden++;
    return hidden;
  });

  // Collapsible section state
  readonly collapsed = signal<Record<string, boolean>>({});
  private readonly sectionKeys = ['callNotif', 'manual', 'busy', 'direct', 'am'];

  readonly allCollapsed = computed(() =>
    this.sectionKeys.every(k => this.collapsed()[k] ?? false),
  );

  private get userAccountId(): number {
    const ps = this.session.selectedPhoneService();
    return ps?.userAccountId ?? 0;
  }

  private phoneServiceId = 0;

  // Expose helpers to template
  readonly formatSchedule = formatSchedule;
  readonly formatDayOfWeek = formatDayOfWeek;

  /** True when a destination is long enough to be abbreviated (e.g. PUSH token) — drives the full-value tooltip. */
  readonly isTokenTruncated = isTruncated;

  /** Colored Type badge — shared with the Reachabilities page. */
  readonly typeBadgeVariant = typeBadgeVariant;

  copyToClipboard(text: string, event?: Event): void {
    copyToClipboard(this.toast, text, event);
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Number(params['phoneServiceId']);
      if (id) {
        this.phoneServiceId = id;
        this.session.selectPhoneServiceById(id);
        this.loadAll();
      }
    });
  }

  toggleSection(key: string): void {
    this.collapsed.update(c => ({ ...c, [key]: !(c[key] ?? false) }));
  }

  toggleAll(): void {
    const shouldCollapse = !this.allCollapsed();
    const next: Record<string, boolean> = {};
    for (const k of this.sectionKeys) next[k] = shouldCollapse;
    this.collapsed.set(next);
  }

  isCollapsed(key: string): boolean {
    return this.collapsed()[key] ?? false;
  }

  loadAll(): void {
    this.loading.set(true);
    this.error.set(null);

    // Run numbers + main notifications/forwardings in parallel — they're independent.
    // Only direct forwardings + AM notifications depend on numbers.
    forkJoin({
      numbers: this.ccApi.getNumbers(this.phoneServiceId).pipe(catchError(() => of([]))),
      callNotif: this.api.getCallNotifications(this.phoneServiceId).pipe(catchError(() => of([]))),
      manual: this.api.getManualForwardings(this.phoneServiceId).pipe(catchError(() => of([]))),
      busy: this.api.getBusyForwardings(this.phoneServiceId).pipe(catchError(() => of([]))),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.phoneNumbers.set(result.numbers);
        this.callNotifications.set(result.callNotif);
        this.manualForwardings.set(this.sortByOrder(result.manual));
        this.busyForwardings.set(this.sortByOrder(result.busy));
        this.loading.set(false);

        // Load number-dependent data after numbers resolve
        this.loadDirectForwardings(this.directForwardingNumbers());

        const hasAm = result.numbers.some(n => n.type === 'VOICEBOX');
        if (hasAm) {
          this.api.getAmNotifications(this.userAccountId).pipe(
            catchError(() => of([])),
            takeUntilDestroyed(this.destroyRef),
          ).subscribe(am => this.amNotifications.set(am));
        } else {
          this.amNotifications.set([]);
        }
      },
      error: (err) => {
        this.error.set(extractErrorMessage(err, 'Failed to load data'));
        this.loading.set(false);
      },
    });
  }

  private loadDirectForwardings(nums: DirectForwardingNumber[]): void {
    if (nums.length === 0) {
      this.directForwardingGroups.set([]);
      return;
    }

    const obs = nums.map(n =>
      this.api.getDirectForwardings(this.phoneServiceId, n.number).pipe(
        catchError(() => of([] as Forwarding[])),
      ),
    );

    forkJoin(obs).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {
      const groups: DirectForwardingGroup[] = nums.map((n, i) => ({
        number: n.number,
        type: n.type,
        tags: n.tags,
        forwardings: this.sortByOrder(results[i]),
      }));
      this.directForwardingGroups.set(groups);
    });
  }

  private sortByOrder(list: Forwarding[]): Forwarding[] {
    return [...list].sort((a, b) => a.order - b.order);
  }

  // ========== Dialog-based Add flow ==========

  openAddDialog(section: string, sectionLabel: string, ccNumber?: string): void {
    // Load addable list first, then open dialog
    let addableObs;
    switch (section) {
      case 'callNotif':
        addableObs = this.api.getAddableCallNotificationReachabilities(this.phoneServiceId);
        break;
      case 'manual':
        addableObs = this.api.getAddableManualReachabilities(this.phoneServiceId);
        break;
      case 'busy':
        addableObs = this.api.getAddableBusyReachabilities(this.phoneServiceId);
        break;
      case 'direct':
        addableObs = ccNumber
          ? this.api.getAddableDirectReachabilities(this.phoneServiceId, ccNumber)
          : of([]);
        break;
      case 'am':
        addableObs = this.api.getAddableAmReachabilities(this.userAccountId);
        break;
      default:
        return;
    }

    addableObs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(addableList => {
      const ref = this.dialog.open(AddReachabilityDialogComponent, {
        data: { addableList, sectionLabel, allowedTypes: SECTION_ALLOWED_TYPES[section] },
        disableClose: true,
      });

      ref.afterClosed().subscribe((result: AddReachabilityDialogResult | null) => {
        if (!result) return;

        if (result.mode === 'existing') {
          this.addExistingToSection(section, result.reachabilityId, ccNumber);
        } else {
          this.createAndAddToSection(section, result, ccNumber);
        }
      });
    });
  }

  private addExistingToSection(section: string, reachId: number, ccNumber?: string): void {
    this.addToSection(section, reachId, ccNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          /** @ticket EB-3798 — usage: an existing reachability was added to a forwarding/notification section (D9). */
          this.loggingService.usage('action.invoke', { action: 'forwarding_add', outcome: 'success' });
          this.reloadAfterAdd(section);
          this.toast.open('Added', 'OK', { duration: 3000 });
        },
        error: (err) => this.toast.open(extractErrorMessage(err, 'Failed to add'), 'OK', SNACKBAR_ERROR_CONFIG),
      });
  }

  private createAndAddToSection(
    section: string,
    result: {
      name: string;
      destination: string;
      type: string;
      publiclyAvailable: boolean;
      callNotificationEmailFormat: string | null;
    },
    ccNumber?: string,
  ): void {
    // Forward the dialog's type-specific settings verbatim to the BE.
    // publiclyAvailable is now user-controlled (default off), and
    // callNotificationEmailFormat is only populated for EMAIL.
    this.reachApi.create(this.phoneServiceId, {
      name: result.name,
      destination: result.destination,
      type: result.type as ReachabilityType,
      publiclyAvailable: result.publiclyAvailable,
      callNotificationEmailFormat: result.callNotificationEmailFormat,
    }).pipe(
      switchMap((created: Reachability) => {
        const reachId = created.id;
        // Guard against an unmapped/missing id: firing the add with an undefined
        // reachId would POST `{ reachability: {} }` and the BE would answer with
        // a cryptic 400/500 (EB-4148). Fail fast with a clear message instead.
        if (!reachId) {
          return throwError(() => new Error(REACHABILITY_NO_ID_ERROR));
        }
        return this.addToSection(section, reachId, ccNumber);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        /** @ticket EB-3798 — usage: a new reachability was created and added to a forwarding/notification section (D9). */
        this.loggingService.usage('action.invoke', { action: 'forwarding_add', outcome: 'success' });
        this.reloadAfterAdd(section);
        this.toast.open('Created and added', 'OK', { duration: 3000 });
      },
      error: (err) => this.toast.open(extractErrorMessage(err, 'Failed to create and add'), 'OK', SNACKBAR_ERROR_CONFIG),
    });
  }

  private addToSection(section: string, reachId: number, ccNumber?: string): Observable<unknown> {
    switch (section) {
      case 'callNotif':
        return this.api.addCallNotification(this.phoneServiceId, reachId);
      case 'manual': {
        const minOrder = this.manualForwardings().length > 0
          ? Math.min(...this.manualForwardings().map(f => f.order)) - 1 : 0;
        return this.api.addManualForwarding(this.phoneServiceId, reachId, minOrder);
      }
      case 'busy': {
        const minOrd = this.busyForwardings().length > 0
          ? Math.min(...this.busyForwardings().map(f => f.order)) - 1 : 0;
        return this.api.addBusyForwarding(this.phoneServiceId, reachId, minOrd);
      }
      case 'direct': {
        const group = this.directForwardingGroups().find(g => g.number === ccNumber);
        const minOrdD = group?.forwardings?.length
          ? Math.min(...group.forwardings.map(f => f.order)) - 1 : 0;
        return ccNumber
          ? this.api.addDirectForwarding(this.phoneServiceId, ccNumber, reachId, minOrdD)
          : of(null);
      }
      case 'am':
        return this.api.addAmNotification(this.userAccountId, reachId);
      default:
        return of(null);
    }
  }

  private reloadAfterAdd(section: string): void {
    if (section === 'direct') {
      this.loadDirectForwardings(this.directForwardingNumbers());
    } else {
      this.reloadSection(section);
    }
  }

  // ========== Delete flows ==========

  deleteNotification(n: Notification, section: 'callNotif' | 'am'): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Notification',
        message: `Delete notification "${n.reachability.name}" (${n.reachability.destination})?`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      const obs = section === 'callNotif'
        ? this.api.deleteCallNotification(this.phoneServiceId, n.notificationId)
        : this.api.deleteAmNotification(this.userAccountId, n.notificationId);
      obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.reloadSection(section);
          this.toast.open('Notification deleted', 'OK', { duration: 3000 });
        },
        error: (err) => this.toast.open(extractErrorMessage(err, 'Failed to delete notification'), 'OK', SNACKBAR_ERROR_CONFIG),
      });
    });
  }

  deleteForwarding(f: Forwarding, section: 'manual' | 'busy'): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Forwarding',
        message: `Delete forwarding "${f.reachability.name}" (${f.reachability.destination})?`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    ref.afterClosed().subscribe(confirmed => {
      // A reorder that started while the dialog was open still holds the lock (EB-4395).
      if (!confirmed || this.reorderBlocksDelete(section)) return;
      const obs = section === 'manual'
        ? this.api.deleteManualForwarding(this.phoneServiceId, f.forwardingId)
        : this.api.deleteBusyForwarding(this.phoneServiceId, f.forwardingId);
      obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.reloadSection(section);
          this.toast.open('Forwarding deleted', 'OK', { duration: 3000 });
        },
        error: (err) => this.toast.open(extractErrorMessage(err, 'Failed to delete forwarding'), 'OK', SNACKBAR_ERROR_CONFIG),
      });
    });
  }

  deleteDirectForwarding(f: Forwarding, ccNumber: string): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Direct Forwarding',
        message: `Delete forwarding "${f.reachability.name}" (${f.reachability.destination}) for number ${ccNumber}?`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.api.deleteDirectForwarding(this.phoneServiceId, ccNumber, f.forwardingId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.loadDirectForwardings(this.directForwardingNumbers());
            this.toast.open('Forwarding deleted', 'OK', { duration: 3000 });
          },
          error: (err) => this.toast.open(extractErrorMessage(err, 'Failed to delete forwarding'), 'OK', SNACKBAR_ERROR_CONFIG),
        });
    });
  }

  /**
   * Bulk-delete every item in a flat section (old CMT "delete all") — confirmed,
   * then fired as parallel single deletes (no bulk endpoint exists).
   */
  deleteAllInSection(section: 'callNotif' | 'manual' | 'busy' | 'am'): void {
    const { label, deletes } = this.sectionDeleteAll(section);
    if (deletes.length === 0) return;

    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Delete all ${label}`,
        message: `Delete all ${deletes.length} ${label.toLowerCase()}? This cannot be undone.`,
        confirmLabel: 'Delete all',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed || this.reorderBlocksDelete(section)) return;
      forkJoin(deletes).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.reloadSection(section);
          this.toast.open(`All ${label.toLowerCase()} deleted`, 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.reloadSection(section);
          this.toast.open(extractErrorMessage(err, 'Failed to delete all'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
    });
  }

  /**
   * Deletes in the reorderable sections wait for a running reorder (one lock for manual + busy, so a Manual reorder also holds Busy deletes until it lands): a PUT could hit a row just
   * deleted, and the section reload would race the reorder reload (a deleted row could reappear).
   * @ticket EB-4395
   */
  private reorderBlocksDelete(section: 'callNotif' | 'manual' | 'busy' | 'am'): boolean {
    if (!this.reordering() || (section !== 'manual' && section !== 'busy')) return false;
    this.toast.open('A reorder is still being saved — try again in a moment', 'OK', { duration: 3000, tone: 'default' });
    return true;
  }

  private sectionDeleteAll(section: 'callNotif' | 'manual' | 'busy' | 'am'): { label: string; deletes: Observable<void>[] } {
    switch (section) {
      case 'callNotif':
        return { label: 'Call Notifications', deletes: this.callNotifications().map(n => this.api.deleteCallNotification(this.phoneServiceId, n.notificationId)) };
      case 'manual':
        return { label: 'Manual Forwardings', deletes: this.manualForwardings().map(f => this.api.deleteManualForwarding(this.phoneServiceId, f.forwardingId)) };
      case 'busy':
        return { label: 'Busy Forwardings', deletes: this.busyForwardings().map(f => this.api.deleteBusyForwarding(this.phoneServiceId, f.forwardingId)) };
      case 'am':
        return { label: 'AM Notifications', deletes: this.amNotifications().map(n => this.api.deleteAmNotification(this.userAccountId, n.notificationId)) };
    }
  }

  /** Bulk-delete all direct forwardings for one number — confirmed. */
  deleteAllDirect(group: DirectForwardingGroup): void {
    if (group.forwardings.length === 0) return;
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete all forwardings',
        message: `Delete all ${group.forwardings.length} direct forwardings for number ${group.number}? This cannot be undone.`,
        confirmLabel: 'Delete all',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      const deletes = group.forwardings.map(f => this.api.deleteDirectForwarding(this.phoneServiceId, group.number, f.forwardingId));
      forkJoin(deletes).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.loadDirectForwardings(this.directForwardingNumbers());
          this.toast.open('All forwardings deleted', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.loadDirectForwardings(this.directForwardingNumbers());
          this.toast.open(extractErrorMessage(err, 'Failed to delete all'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
    });
  }

  // ========== Reorder ==========

  /**
   * Move a manual/busy forwarding one place up or down (old CMT `moveItem`).
   * `visible` is the list as rendered — under an active filter the swap partner is the
   * visible neighbour, not a hidden row in between. The order sequence is checked and,
   * if broken, repaired over the FULL section list (see `planOrderSwap`).
   * @ticket EB-4395
   */
  moveUp(f: Forwarding, visible: Forwarding[], section: 'manual' | 'busy'): void {
    const index = visible.findIndex(v => v.forwardingId === f.forwardingId);
    if (index <= 0) return;
    this.swapOrder(f, visible[index - 1], section);
  }

  moveDown(f: Forwarding, visible: Forwarding[], section: 'manual' | 'busy'): void {
    const index = visible.findIndex(v => v.forwardingId === f.forwardingId);
    if (index < 0 || index >= visible.length - 1) return;
    this.swapOrder(f, visible[index + 1], section);
  }

  private swapOrder(a: Forwarding, b: Forwarding, section: 'manual' | 'busy'): void {
    if (this.reordering()) return;
    const full = section === 'manual' ? this.manualForwardings() : this.busyForwardings();
    // Intentional deviation: old CMT's busy-forwarding repair writes through the MANUAL endpoint
    // (busy.forwarding.dir.js → updateManualForwarding), a bug. AMIE writes each section through
    // its own endpoint — do not "restore" parity here (Torsten, EB-4395).
    const updates = planOrderSwap(full, a, b).map(u => section === 'manual'
      ? this.api.updateManualForwarding(this.phoneServiceId, u.forwardingId, u)
      : this.api.updateBusyForwarding(this.phoneServiceId, u.forwardingId, u));
    if (updates.length === 0) return;

    const reload$ = section === 'manual'
      ? this.api.getManualForwardings(this.phoneServiceId).pipe(tap(list => this.manualForwardings.set(this.sortByOrder(list))))
      : this.api.getBusyForwardings(this.phoneServiceId).pipe(tap(list => this.busyForwardings.set(this.sortByOrder(list))));

    /**
     * The lock is held until the RELOAD has landed, not just the writes: a second move planned
     * on the pre-reload list would swap stale order values. Every write settles before the
     * reload — a plain `forkJoin` would abort the remaining PUTs of a repair on the first failure
     * and leave their state unknown (old CMT's `$q.all` rejected without cancelling). A failed
     * write still reloads, since the others may have landed.
     * @ticket EB-4395
     */
    this.reordering.set(true);
    const settled = updates.map(u => u.pipe(map(() => null), catchError((err: unknown) => of(err))));
    forkJoin(settled).pipe(
      tap(results => {
        const failures = results.filter(r => r !== null);
        if (failures.length === 0) {
          this.loggingService.usage('action.invoke', { action: 'forwarding_reorder', outcome: 'success' });
          return;
        }
        const message = results.length === 1
          ? extractErrorMessage(failures[0], 'Failed to reorder')
          : `Failed to reorder — ${failures.length} of ${results.length} updates failed`;
        this.toast.open(message, 'OK', SNACKBAR_ERROR_CONFIG);
      }),
      switchMap(() => reload$),
      finalize(() => this.reordering.set(false)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      error: (err) => this.toast.open(extractErrorMessage(err, 'Failed to reload forwardings'), 'OK', SNACKBAR_ERROR_CONFIG),
    });
  }

  // ========== Schedule edit ==========

  editSchedule(f: Forwarding, section: 'busy' | 'direct', ccNumber?: string): void {
    const ref = this.dialog.open(ScheduleEditDialogComponent, {
      data: { forwarding: f },
      disableClose: true,
    });

    ref.afterClosed().subscribe((result: ScheduleEditDialogResult | null) => {
      if (!result) return;
      const updated = { ...f, ...result };

      let obs;
      if (section === 'busy') {
        obs = this.api.updateBusyForwarding(this.phoneServiceId, f.forwardingId, updated);
      } else if (ccNumber) {
        obs = this.api.updateDirectForwarding(this.phoneServiceId, ccNumber, f.forwardingId, updated);
      } else {
        return;
      }

      obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          if (section === 'busy') {
            this.reloadSection('busy');
          } else {
            this.loadDirectForwardings(this.directForwardingNumbers());
          }
          this.toast.open('Schedule updated', 'OK', { duration: 3000 });
        },
        error: (err) => this.toast.open(extractErrorMessage(err, 'Failed to update schedule'), 'OK', SNACKBAR_ERROR_CONFIG),
      });
    });
  }

  // ========== Section reload ==========

  private reloadSection(section: string): void {
    switch (section) {
      case 'callNotif':
        this.api.getCallNotifications(this.phoneServiceId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(list => this.callNotifications.set(list));
        break;
      case 'manual':
        this.api.getManualForwardings(this.phoneServiceId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(list => this.manualForwardings.set(this.sortByOrder(list)));
        break;
      case 'busy':
        this.api.getBusyForwardings(this.phoneServiceId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(list => this.busyForwardings.set(this.sortByOrder(list)));
        break;
      case 'am':
        this.api.getAmNotifications(this.userAccountId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(list => this.amNotifications.set(list));
        break;
    }
  }

  // ========== Filter helpers ==========

  private filterNotifications(list: Notification[]): Notification[] {
    const q = this.filterText().toLowerCase();
    return list.filter(n =>
      this.typeVisible(n.reachability.type) && (!q || this.matchesFilter(n.reachability, q)),
    );
  }

  private filterForwardings(list: Forwarding[]): Forwarding[] {
    const q = this.filterText().toLowerCase();
    return list.filter(f =>
      this.typeVisible(f.reachability.type) && (!q || this.matchesFilter(f.reachability, q)),
    );
  }

  private matchesFilter(r: { name: string; destination: string; type: string }, q: string): boolean {
    return r.name.toLowerCase().includes(q)
      || r.destination.toLowerCase().includes(q)
      || r.type.toLowerCase().includes(q);
  }
}
